import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { Maintenance, SAMPLE_MAINTENANCES, MaintenanceType, MaintenanceStatus, Priority } from '../models/maintenance';
import { padStart } from '../utils/string.utils';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  })
};

@Injectable({
  providedIn: 'root'
})
export class MaintenanceService {
  
  private apiUrl = 'https://formation.hybraine.com/mboufta/backend/api/maintenance.php'; // À adapter selon votre backend
  
  // Pour le développement, on utilise des données en mémoire
  private maintenances: Maintenance[] = [...SAMPLE_MAINTENANCES];

  constructor(private http: HttpClient) { }

  // ============ GESTION DES MAINTENANCES ============

  getMaintenances(): Observable<Maintenance[]> {
    // En production: return this.http.get<Maintenance[]>(`${this.apiUrl}/maintenances`);
    return of(this.maintenances);
  }

  getMaintenanceById(id: number): Observable<Maintenance> {
    const maintenance = this.maintenances.find(m => m.id === id);
    return of(maintenance!);
  }

  getMaintenancesByProperty(propertyId: number): Observable<Maintenance[]> {
    const propertyMaintenances = this.maintenances.filter(m => m.propertyId === propertyId);
    return of(propertyMaintenances);
  }

  getMaintenancesByStatus(status: MaintenanceStatus): Observable<Maintenance[]> {
    const filtered = this.maintenances.filter(m => m.status === status);
    return of(filtered);
  }

  getMaintenancesByPriority(priority: Priority): Observable<Maintenance[]> {
    const filtered = this.maintenances.filter(m => m.priority === priority);
    return of(filtered);
  }

  createMaintenance(maintenance: Maintenance): Observable<Maintenance> {
    const reference = `MAINT-${new Date().getFullYear()}-${padStart(String(this.maintenances.length + 1), 3, '0')}`;
    const newMaintenance = {
      ...maintenance,
      id: Math.max(...this.maintenances.map(m => m.id), 0) + 1,
      reference,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    this.maintenances.push(newMaintenance);
    // En production: return this.http.post<Maintenance>(`${this.apiUrl}/maintenances`, maintenance, httpOptions);
    return of(newMaintenance);
  }

  updateMaintenance(id: number, maintenance: Maintenance): Observable<Maintenance> {
    const index = this.maintenances.findIndex(m => m.id === id);
    if (index !== -1) {
      this.maintenances[index] = { ...maintenance, id, updatedAt: new Date() };
      // En production: return this.http.put<Maintenance>(`${this.apiUrl}/maintenances/${id}`, maintenance, httpOptions);
      return of(this.maintenances[index]);
    }
    return of(maintenance);
  }

  deleteMaintenance(id: number): Observable<boolean> {
    const index = this.maintenances.findIndex(m => m.id === id);
    if (index !== -1) {
      this.maintenances.splice(index, 1);
      // En production: return this.http.delete<boolean>(`${this.apiUrl}/maintenances/${id}`, httpOptions);
      return of(true);
    }
    return of(false);
  }

  searchMaintenances(filters: any): Observable<Maintenance[]> {
    let filtered = [...this.maintenances];
    
    if (filters.type) {
      filtered = filtered.filter(m => m.type === filters.type);
    }
    if (filters.status) {
      filtered = filtered.filter(m => m.status === filters.status);
    }
    if (filters.priority) {
      filtered = filtered.filter(m => m.priority === filters.priority);
    }
    if (filters.propertyId) {
      filtered = filtered.filter(m => m.propertyId === filters.propertyId);
    }
    if (filters.category) {
      filtered = filtered.filter(m => m.category === filters.category);
    }
    
    return of(filtered);
  }

  // ============ STATISTIQUES ============

  getStatistics(): Observable<any> {
    const stats = {
      total: this.maintenances.length,
      enCours: this.maintenances.filter(m => m.status === 'en_cours').length,
      planifiees: this.maintenances.filter(m => m.status === 'planifiee').length,
      terminees: this.maintenances.filter(m => m.status === 'terminee').length,
      urgentes: this.maintenances.filter(m => m.priority === 'urgence').length,
      totalCost: this.maintenances
        .filter(m => m.actualCost)
        .reduce((sum, m) => sum + (m.actualCost || 0), 0),
      estimatedCost: this.maintenances
        .filter(m => m.estimatedCost)
        .reduce((sum, m) => sum + (m.estimatedCost || 0), 0)
    };
    return of(stats);
  }
}
